import React from 'react';
import clsx from 'clsx';
import { XSButton } from '../component/button/XSButton';
import './useTabs.css';
import compact from 'lodash/compact';

type Key = string | number | undefined;

export type UseTabsInput<T> = {
    defaultKey?: Key;
    tabs: (false | Tab)[];
    ui?: {
        type?: UiType;
        shape?: 'square';
        background?: 'none';
        direction?: 'row' | 'column';
        align?: 'start';
        flexChildren?: boolean;
    };
};

type Tab = {
    key: Key;
    label?: React.ReactNode;
    element?: React.ReactNode;
};

type UiType = 'tabs' | 'segment';

export function useTabs<T extends Tab[]>(input: UseTabsInput<T>) {
    const { defaultKey, ui } = input;
    const tabs = compact(input.tabs);
    const [activeKey, setActiveKey] = React.useState(defaultKey);
    const activeTab = getActiveTabByKey(activeKey);

    const uiType: UiType = ui?.type || 'tabs';
    const className = [`XS-useTabs ui-${uiType}`];

    if (ui) {
        if (ui.shape) {
            className.push(`shape-${ui.shape}`);
        }

        if (ui.background) {
            className.push(`background-${ui.background}`);
        }

        if (ui.direction) {
            className.push(`direction-${ui.direction}`);
        }

        if (ui.align) {
            className.push(`align-${ui.align}`);
        }

        if (ui.flexChildren) {
            className.push(`flexChildren`);
        }
    }

    function getActiveTabByKey(key: Key) {
        return tabs.find((tab) => tab.key === key);
    }

    return {
        tabs: (
            <div className={clsx(className)}>
                {tabs.map((tab) => (
                    <XSButton
                        key={tab.key}
                        className={clsx({ active: tab.key === activeKey })}
                        onClick={() => {
                            setActiveKey(tab.key);
                        }}
                        fill={'none'}>
                        {tab.label}
                    </XSButton>
                ))}
            </div>
        ),
        content: activeTab?.element || null,
        activeKey: activeKey,
        setActiveKey: setActiveKey,
    };
}
